import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { txToast } from "@/components/TxToaster";
import { useBackendApi } from "@/hooks/fetch";
import type { AdminGroup, AdminGroupSaveReq, AdminGroupSaveResp } from "@shared/adminApiTypes";
import { useMemo, useState } from "react";
import PermissionPicker from "./PermissionPicker";

const NAME_MIN = 2;
const NAME_MAX = 32;

type Props = {
    /** undefined when creating a new group */
    group?: AdminGroup;
    registeredPermissions: Record<string, string>;
    onClose: () => void;
    onSaved: () => void;
};

/** Returns why the name is unusable, or null when it is fine. */
function validateName(name: string) {
    const trimmed = name.trim();
    if (trimmed.length < NAME_MIN) return `Mindestens ${NAME_MIN} Zeichen.`;
    if (trimmed.length > NAME_MAX) return `Höchstens ${NAME_MAX} Zeichen.`;
    return null;
}

const sameSet = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const bSet = new Set(b);
    return a.every((item) => bSet.has(item));
};

export default function GroupEditDialog({ group, registeredPermissions, onClose, onSaved }: Props) {
    const [name, setName] = useState(group?.name ?? '');
    const [permissions, setPermissions] = useState<string[]>(group?.permissions ?? []);
    const [isSaving, setIsSaving] = useState(false);
    //only shown after a failed submit - complaining while someone is still typing is noise
    const [showErrors, setShowErrors] = useState(false);

    const saveApi = useBackendApi<AdminGroupSaveResp, AdminGroupSaveReq>({
        method: 'POST',
        path: '/adminManager/saveGroup',
    });

    const nameError = validateName(name);
    const isDirty = useMemo(() => {
        if (!group) return true; //a new group is always worth saving
        return group.name !== name.trim() || !sameSet(group.permissions, permissions);
    }, [group, name, permissions]);

    const permissionSummary = permissions.includes('all_permissions')
        ? 'Alle Berechtigungen'
        : `${permissions.length} ausgewählt`;

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (isSaving) return;
        if (nameError) {
            setShowErrors(true);
            return;
        }

        setIsSaving(true);
        saveApi({
            data: { id: group?.id, name: name.trim(), permissions },
            toastLoadingMessage: group ? 'Gruppe wird gespeichert...' : 'Gruppe wird erstellt...',
            success: (data, toastId) => {
                if ('error' in data) {
                    //the backend owns the rules we cannot check here, like a duplicate name
                    txToast.error(data.error, { id: toastId });
                    return;
                }
                txToast.success(group ? 'Gruppe gespeichert.' : 'Gruppe erstellt.', { id: toastId });
                onSaved();
            },
            finally: () => setIsSaving(false),
        });
    };

    return (
        <Dialog open onOpenChange={(open) => !open && !isSaving && onClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{group ? `Gruppe bearbeiten: ${group.name}` : 'Neue Gruppe'}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="contents">
                    <div className="space-y-5 overflow-y-auto pr-2 -mr-2">
                        <div className="space-y-1 max-w-sm">
                            <Label htmlFor="group-name">Name</Label>
                            <Input
                                id="group-name"
                                value={name}
                                maxLength={NAME_MAX}
                                placeholder="z.B. Supporter"
                                disabled={isSaving}
                                autoFocus={!group}
                                onChange={(e) => setName(e.target.value)}
                            />
                            {showErrors && nameError ? (
                                <p className="text-caption text-danger-soft">{nameError}</p>
                            ) : null}
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-baseline justify-between gap-2">
                                <h3 className="font-semibold">Berechtigungen</h3>
                                <span className="text-caption text-faint">{permissionSummary}</span>
                            </div>
                            <PermissionPicker
                                registered={registeredPermissions}
                                value={permissions}
                                onChange={setPermissions}
                                disabled={isSaving}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="muted" onClick={onClose} disabled={isSaving}>
                            Abbrechen
                        </Button>
                        <Button type="submit" variant="solid" disabled={isSaving || !isDirty}>
                            {group ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
